import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, map, Observable, of, tap, finalize, catchError, shareReplay } from 'rxjs';
import { HttpClient, HttpEvent, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { usTimeZoneList } from '../_data/time-zones';
import { 
  CashDrawerConfig, 
  CashDropConfig, 
  CashHandlingConfig, 
  CustomerGroupConfig, 
  PrintingConfig, 
  SecurityConfig, 
  TaxConfig, 
  TenderScreenConfig, 
  TimeTrackingConfig 
} from '../_models/configuration-widgets/business-config';
import { LoyaltyConfig } from '../_models/loyalty-config';
import { AppBaseDataService } from './app-base-data.service';
import { BaseService } from './base.service';
import { GlobalService } from './global.service';

// Interfaces
export interface EnableDisableWidgetView {
  partialText: string;
  partialBorder: string;
  partialIcon: string;
}

export interface BusinessSetupActiveItem {
  UserID: string;
  businessType: string;
  industryType: string;
  ContactEmail: string;
  HasPhysicalStore: boolean;
  HasMobileApp: boolean;
  HasOnlineWebsite: boolean;
  HasPOS: boolean;
  StandardsForms: {
    TimeZone: string;
    MeasuringUnit: number;
    WeightUnit: number;
    Languages: any[];
    Currencies: any[];
  };
}

export interface WeightUnit {
  id: number;
  value: string;
}

export interface StoreContentClientStatePatch {
  availSaveOffer?: boolean;
  storeContentStatus?: number | null;
  storeContentFileUrl?: string;
  storeContentShowNotificationBar?: boolean | null;
}

// Matches the backend `OnlineStoreSetupStatus` field on BusinessSetup — a string enum, not the
// numeric StoreContentStatus scheme used for the "Store Content" offer.
export type OnlineStoreSetupStatus = 'Pending' | 'InProgress' | 'Completed';

export interface OnlineStoreCreatedPayload {
  userId: string;
  businessId: number | string;
  onlineStoreUrl: string;
  onlineStoreName?: string;
  onlineStoreTemplateId?: number | string;
  onlineStoreTemplateLabel?: string;
  onlineStoreTemplateThumbnailUrl?: string;
  onlineStoreTemplateCategory?: string;
  onlineStoreTemplateDemoUrl?: string;
  onlineStoreTemplatePrimaryColor?: string;
  onlineStoreTemplatePreviewImages?: string[];
  brandColors?: string[];
  webStoreId?: string;
  demoUrl?: string;
  imageUrl?: string;
}

export interface CreateB2CSiteTemplateMeta {
  templateLabel?: string;
  templateThumbnailUrl?: string;
  templateCategory?: string;
  templateDemoUrl?: string;
  templatePrimaryColor?: string;
  templatePreviewImages?: string[];
}

// Enums
enum WidgetStatus {
  DEFAULT = 0,
  ENABLED = 1,
  DISABLED = 2
}

enum WidgetType {
  TAX = 'tax',
  INVOICE = 'invoice',
  PRINTING = 'printing',
  CASHDROP = 'cashdrop',
  CASHDRAWER = 'cashdrawer',
  SECURITY = 'security',
  TENDER = 'tender'
}
/**
 * BusinessSetupService
 * 
 * A comprehensive service for managing business setup and configuration processes.
 * This service handles:
 * 
 * 1. Widget Management:
 *    - Tax configuration widgets
 *    - Invoice/cash handling widgets  
 *    - Printing configuration widgets
 *    - Security configuration widgets
 *    - Cash drop/drawer configuration widgets
 *    - Tender screen configuration widgets
 * 
 * 2. Business Information:
 *    - Business setup data management
 *    - Location and timezone settings
 *    - Standards and measurement units
 * 
 * 3. Configuration APIs:
 *    - Payment processing configuration
 *    - Shipping configuration
 *    - Loyalty program settings
 *    - Theme configuration
 *    - Secondary screen settings
 * 
 * Features:
 * - Centralized widget state management using BehaviorSubjects
 * - Consistent HTTP service methods with error handling
 * - Type-safe interfaces and enums
 * - Reactive programming patterns with RxJS
 * 
 * @author Business Setup Team
 * @version 2.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class BusinessSetupService {
  activeTab: any = 'configuration-setup'
  // activeTab: any = 'general-info-1'
  usTimeZones = usTimeZoneList
  searchText = ''
  selectedTimeZone: any = ''

  selectedState: any = ''
  city: any = ''

  isDisableNextButton=false
  isChooseLater:boolean = false;
  selectedTheme:any =null;
  // User dialog options
  showNewEditUser = false
  isEditLocation = false
  isNew = false

    showTaxSetting = false;
    taxSettingVisibilityChange: Subject<boolean> = new Subject<boolean>();

  showInvoiceConfiguration = false;
  showPrintingConfiguration = false;
  showSecurityConfiguration = false;
  showCashdropConfiguration = false;
  showCashdrawerConfiguration = false;
  showTenderConfiguration = false;

  featureImage: any = null
  thImages: any = []
  thImageNames: any = []

  // BehaviorSubjects to store widget icon CSS information
  partialTextOptions = {0:'', 1:'enabled-text', 2:'disabled-text'}
  partialBorderOptions = {0: '', 1: 'enabled-border', 2: 'disabled-border'}
  partialIconOptions = {0: '', 1: 'enabled-icon', 2: 'disabled-icon'}

  // Consolidated options for widget styling
  private readonly PARTIAL_OPTIONS = {
    text: this.partialTextOptions,
    border: this.partialBorderOptions,
    icon: this.partialIconOptions
  };

  // Widget subjects map for centralized management
  private readonly widgetSubjects = new Map<string, BehaviorSubject<EnableDisableWidgetView>>([
    [WidgetType.TAX, new BehaviorSubject<EnableDisableWidgetView>({partialBorder: '', partialIcon: '', partialText: ''})],
    [WidgetType.INVOICE, new BehaviorSubject<EnableDisableWidgetView>({partialBorder: '', partialIcon: '', partialText: ''})],
    [WidgetType.PRINTING, new BehaviorSubject<EnableDisableWidgetView>({partialBorder: '', partialIcon: '', partialText: ''})],
    [WidgetType.CASHDROP, new BehaviorSubject<EnableDisableWidgetView>({partialBorder: '', partialIcon: '', partialText: ''})],
    [WidgetType.CASHDRAWER, new BehaviorSubject<EnableDisableWidgetView>({partialBorder: '', partialIcon: '', partialText: ''})],
    [WidgetType.SECURITY, new BehaviorSubject<EnableDisableWidgetView>({partialBorder: '', partialIcon: '', partialText: ''})],
    [WidgetType.TENDER, new BehaviorSubject<EnableDisableWidgetView>({partialBorder: '', partialIcon: '', partialText: ''})]
  ]);

  // Public observables for widgets
  public taxWidgetBS$ = this.widgetSubjects.get(WidgetType.TAX)!.asObservable();
  public invoiceWidgetBS$ = this.widgetSubjects.get(WidgetType.INVOICE)!.asObservable();
  public printingWidgetBS$ = this.widgetSubjects.get(WidgetType.PRINTING)!.asObservable();
  public cashdropWidgetBS$ = this.widgetSubjects.get(WidgetType.CASHDROP)!.asObservable();
  public cashdrawerWidgetBS$ = this.widgetSubjects.get(WidgetType.CASHDRAWER)!.asObservable();
  public securityWidgetBS$ = this.widgetSubjects.get(WidgetType.SECURITY)!.asObservable();
  public tenderWidgetBS$ = this.widgetSubjects.get(WidgetType.TENDER)!.asObservable();
  private readonly storeContentStateRefreshSubject = new Subject<void>();
  public readonly storeContentStateRefresh$ = this.storeContentStateRefreshSubject.asObservable();
  private setOnlineStoreInitiatedInFlight = false;



  _globals = GlobalService

  weightUnitList = [
    {
      id: 1,
      value: "Kg"
    },
    {
      id: 2,
      value: "Gram"
    }
  ]

  activeItem: any = {
    "UserID": '',
    "businessType": '',
    "industryType": '',
    "ContactEmail": '',
    "HasPhysicalStore": false,
    "HasMobileApp": false,
    "HasOnlineWebsite": false,
    "HasPOS": false,
    "StandardsForms": {
      "TimeZone": '',
      "MeasuringUnit": 0,
      "WeightUnit": 0,
      "Languages": [],
      "Currencies": []
    }
  }

  constructor(
    private _abd: AppBaseDataService,
    private _bs: BaseService,
    private http: HttpClient
  ) {
    this.initializeService();
  }

  notifyStoreContentStateRefresh(): void {
    this.storeContentStateRefreshSubject.next();
  }

  persistStoreContentClientState(patch: StoreContentClientStatePatch): void {
    const normalizedPatch = this.buildStoreContentClientStatePatch(patch);
    if (Object.keys(normalizedPatch).length === 0) {
      this.notifyStoreContentStateRefresh();
      return;
    }

    const currentSetup = GlobalService.userInfo?.businessSetup?.[0];
    if (currentSetup) {
      Object.assign(currentSetup, normalizedPatch);
    }

    const sessionSetup = this.getBusinessInfoFromSession();
    if (sessionSetup) {
      try {
        sessionStorage.setItem('business-Info', JSON.stringify({ ...sessionSetup, ...normalizedPatch }));
      } catch {
        // Ignore storage errors.
      }
    }

    this.notifyStoreContentStateRefresh();
  }

  private buildStoreContentClientStatePatch(patch: StoreContentClientStatePatch): Record<string, any> {
    const normalizedPatch: Record<string, any> = {};

    if (patch.availSaveOffer !== undefined) {
      normalizedPatch.availSaveOffer = patch.availSaveOffer;
      normalizedPatch.AvailSaveOffer = patch.availSaveOffer;
    }

    if (patch.storeContentStatus !== undefined) {
      normalizedPatch.storeContentStatus = patch.storeContentStatus;
      normalizedPatch.StoreContentStatus = patch.storeContentStatus;
    }

    if (patch.storeContentFileUrl !== undefined) {
      normalizedPatch.storeContentFileUrl = patch.storeContentFileUrl;
      normalizedPatch.StoreContentFileUrl = patch.storeContentFileUrl;
    }

    if (patch.storeContentShowNotificationBar !== undefined && patch.storeContentShowNotificationBar !== null) {
      normalizedPatch.storeContentShowNotificationBar = patch.storeContentShowNotificationBar;
      normalizedPatch.StoreContentShowNotificationBar = patch.storeContentShowNotificationBar;
    }

    return normalizedPatch;
  }

  private getBusinessInfoFromSession(): any {
    try {
      return JSON.parse(sessionStorage.getItem('business-Info') || 'null');
    } catch {
      return null;
    }
  }

  // Private helper methods
  private getDefaultActiveItem(): BusinessSetupActiveItem {
    return {
      UserID: '',
      businessType: '',
      industryType: '',
      ContactEmail: '',
      HasPhysicalStore: false,
      HasMobileApp: false,
      HasOnlineWebsite: false,
      HasPOS: false,
      StandardsForms: {
        TimeZone: '',
        MeasuringUnit: 0,
        WeightUnit: 0,
        Languages: [],
        Currencies: []
      }
    };
  }

  private initializeService(): void {
    try {
      this.selectedTimeZone = this.usTimeZones[0];
      this.activeItem = this.createActiveItemFromGlobals();
      this.setupSubscriptions();
    } catch (error) {
      //console.error('Error initializing BusinessSetupService:', error);
    }
  }

  private createActiveItemFromGlobals(): BusinessSetupActiveItem {
    const globals = GlobalService;
    if (!globals.userInfo?.businessSetup?.[0]) {
      return this.getDefaultActiveItem();
    }

    const businessSetup = globals.userInfo.businessSetup[0];
    return {
      UserID: globals.userInfo.userID,
      businessType: businessSetup.businessType,
      industryType: businessSetup.industry,
      ContactEmail: globals.userInfo.email,
      HasPhysicalStore: businessSetup.hasPhysicalStore,
      HasMobileApp: businessSetup.hasMobileApp,
      HasOnlineWebsite: businessSetup.hasOnlineWebsite,
      HasPOS: businessSetup.hasPOS,
      StandardsForms: {
        TimeZone: this.usTimeZones[0].value,
        MeasuringUnit: businessSetup.standardsForms.measuringUnit,
        WeightUnit: businessSetup.standardsForms.weightUnit,
        Languages: businessSetup.standardsForms.languages,
        Currencies: businessSetup.standardsForms.currencies
      }
    };
  }

  private setupSubscriptions(): void {
    this.taxSettingVisibilityChange.subscribe((value) => {
      this.showTaxSetting = value;
    });
  }



  // Public methods
  initActiveItem(): void {
    const globals = GlobalService;
    const businessSetup = globals.userInfo?.businessSetup?.[0];
    
    if (!businessSetup) {
      console.warn('Business setup data not available');
      return;
    }

    const tz = this.usTimeZones.find(f => f.value === businessSetup.standardsForms.timeZone);
    
    GlobalService.sleep(100).then(() => {
      this.activeItem = {
        UserID: globals.userInfo.userID,
        businessType: businessSetup.businessType,
        industryType: businessSetup.industry,
        ContactEmail: globals.userInfo.email,
        HasPhysicalStore: businessSetup.hasPhysicalStore,
        HasMobileApp: businessSetup.hasMobileApp,
        HasOnlineWebsite: businessSetup.hasOnlineWebsite,
        HasPOS: businessSetup.hasPOS,
        StandardsForms: {
          TimeZone: tz?.value || '',
          MeasuringUnit: businessSetup.standardsForms.measuringUnit,
          WeightUnit: businessSetup.standardsForms.WeightUnit,
          Languages: businessSetup.standardsForms.languages,
          Currencies: businessSetup.standardsForms.currencies
        }
      };
    });
  }

  /**
   * Initializes a specific widget with its current state
   * @param widgetName - The name of the widget to initialize
   */
  initWidget(widgetName: string): void {
    if (!Object.values(WidgetType).includes(widgetName as WidgetType)) {
      console.warn(`Invalid widget name: ${widgetName}`);
      return;
    }

    const widgetSubject = this.widgetSubjects.get(widgetName);
    if (!widgetSubject) {
      console.warn(`Widget subject not found for: ${widgetName}`);
      return;
    }

    // Reset widget state
    widgetSubject.next({ partialBorder: '', partialIcon: '', partialText: '' });

    try {
      const { activeValue, savedValue } = this.getWidgetValues(widgetName as WidgetType);
      this.setWidgetStatusHelperFunction(activeValue, savedValue, widgetName);
    } catch (error) {
      console.error(`Error initializing widget ${widgetName}:`, error);
      // Set to default state on error
      widgetSubject.next({ partialBorder: '', partialIcon: '', partialText: '' });
    }
  }

  /**
   * Retrieves active and saved values for a specific widget type
   * @param widgetType - The type of widget to get values for
   * @returns Object containing activeValue and savedValue booleans
   */
  private getWidgetValues(widgetType: WidgetType): { activeValue: boolean; savedValue: boolean } {
    try {
      const configs = this._abd.systemConfigs;
      
      if (!configs) {
        console.warn('System configs not available');
        return { activeValue: false, savedValue: false };
      }

      const configMap = {
        [WidgetType.TAX]: configs.taxConfig,
        [WidgetType.INVOICE]: configs.cashHandlingConfig,
        [WidgetType.PRINTING]: configs.printingConfig,
        [WidgetType.CASHDROP]: configs.cashDropConfig,
        [WidgetType.CASHDRAWER]: configs.cashDrawerConfig,
        [WidgetType.SECURITY]: configs.securityConfig,
        [WidgetType.TENDER]: configs.tenderScreenConfig
      };

      const config = configMap[widgetType];
      
      if (!config) {
        console.warn(`Config not found for widget type: ${widgetType}`);
        return { activeValue: false, savedValue: false };
      }

      return {
        activeValue: config.active || false,
        savedValue: config.isSaved || false
      };
    } catch (error) {
      console.error(`Error getting widget values for ${widgetType}:`, error);
      return { activeValue: false, savedValue: false };
    }
  }

  setWidgetStatusHelperFunction(activeValue: boolean, savedValue: boolean, widgetName: string): void {
    const widgetSubject = this.widgetSubjects.get(widgetName);
    if (!widgetSubject) {
      console.warn(`Widget subject not found for: ${widgetName}`);
      return;
    }

    let status: WidgetStatus;
    
    // Determine widget status based on active and saved values
    if (savedValue && activeValue) {
      status = WidgetStatus.ENABLED;
    } else if (savedValue && !activeValue) {
      status = WidgetStatus.DISABLED;
    } else {
      status = WidgetStatus.DEFAULT;
    }

    // Update widget appearance
    widgetSubject.next({
      partialBorder: this.PARTIAL_OPTIONS.border[status] + (status !== WidgetStatus.DEFAULT ? '-' + widgetName : ''),
      partialIcon: this.PARTIAL_OPTIONS.icon[status] + (status !== WidgetStatus.DEFAULT ? '-' + widgetName : ''),
      partialText: this.PARTIAL_OPTIONS.text[status]
    });
  }

  getWidget(widgetName: string): BehaviorSubject<EnableDisableWidgetView> | undefined {
    return this.widgetSubjects.get(widgetName);
  }

  // Widget management methods
  resetAllWidgets(): void {
    this.widgetSubjects.forEach((subject) => {
      subject.next({ partialBorder: '', partialIcon: '', partialText: '' });
    });
  }

  initializeAllWidgets(): void {
    Object.values(WidgetType).forEach(widgetType => {
      this.initWidget(widgetType);
    });
  }

  getWidgetStatus(widgetName: string): EnableDisableWidgetView | null {
    const widget = this.widgetSubjects.get(widgetName);
    return widget ? widget.value : null;
  }

  // HTTP service methods with improved error handling and consistency
  
  /**
   * Generic method for business setup HTTP POST operations
   */
  private businessHttpPost<T>(endpoint: string, value: T, showSpinner: boolean = true): Observable<any> {
    const url = environment.BusinessBaseURL + endpoint;
    return this._bs.httpPost(url, value, false, showSpinner).pipe(
      map(res => res),
      // You can add error handling here if needed
    );
  }

  /**
   * Generic method for business setup HTTP GET operations
   */
  private businessHttpGet(endpoint: string, skipInterceptor: boolean = false): Observable<any> {
    const url = environment.BusinessBaseURL + endpoint;
    return this._bs.httpGet(url, true, null, skipInterceptor).pipe(
      map(res => res),
      // You can add error handling here if needed
    );
  }

  /**
   * Generic method for business setup HTTP PUT operations
   */
  private businessHttpPut<T>(endpoint: string, value: T): Observable<any> {
    const url = environment.BusinessBaseURL + endpoint;
    return this._bs.httpPut(url, value).pipe(
      map(res => res),
      // You can add error handling here if needed
    );
  }

  /**
   * Generic method for business setup HTTP DELETE operations
   */
  private businessHttpDelete(endpoint: string): Observable<any> {
    const url = environment.BusinessBaseURL + endpoint;
    return this._bs.httpDelete(url).pipe(
      map(res => res),
      // You can add error handling here if needed
    );
  }

  // Configuration methods
  saveSecondaryScreenSettings(value: any): Observable<any> {
    return this.businessHttpPost("SetContentData", value);
  }

  getShippingConfiguration(skipInterceptor: boolean = false): Observable<any> {
    return this.businessHttpGet("webstore/shipping", skipInterceptor);
  }

  /** Emits after a webstore/shipping zone is saved or updated successfully. BusinessSetupProgressService
   *  listens to this, so the Shipping step completes no matter which screen saved the zone. */
  readonly shippingSaved$ = new Subject<void>();

  saveShippingConfiguration(value: any): Observable<any> {
    return this.businessHttpPost("webstore/shipping", value).pipe(tap(res => this.emitIfShippingSaved(res)));
  }

  updateShippingConfiguration(id: string, value: any): Observable<any> {
    const endpoint = id !== null ? `webstore/shipping/${id}` : "webstore/shipping";
    return this.businessHttpPut(endpoint, value).pipe(tap(res => this.emitIfShippingSaved(res)));
  }

  private emitIfShippingSaved(res: any): void {
    if (res?.success) this.shippingSaved$.next();
  }

  deleteShippingConfiguration(id: string): Observable<any> {
    return this.businessHttpDelete(`webstore/shipping/${id}`);
  }

  getPaymentProcessing(skipInterceptor: boolean = false): Observable<any> {
    return this.businessHttpGet("paymentconfig", skipInterceptor);
  }

  getPaymentProcessingStatus(): Observable<any> {
    return this.businessHttpGet("paymentconfig/status");
  }

  updatePaymentProcessing(id: any, value: any): Observable<any> {
    return this.businessHttpPut(`paymentconfig/${id}`, value);
  }

  getSecondaryScreenSettings(): Observable<any> {
    return this.businessHttpGet("GetContentData?locationId=0&platform=POSAPP&useType=Promotional");
  }

  getStoreContentSubmission(): Observable<any> {
    return this.businessHttpGet("GetContentData?locationId=0&platform=WEBSTORE&useType=StoreContentSubmission", true);
  }

  getStoreContentBannerSettings(): Observable<any> {
    return this.businessHttpGet("GetContentData?locationId=0&platform=WEBSTORE&useType=StoreContentBanner", true);
  }

  uploadStoreContentFileWithProgress(value: any): Observable<HttpEvent<any>> {
    const url = environment.BusinessBaseURL + (environment.UploadStoreContentFile || "StoreContent/Upload");
    let headers = this._bs.GetAuthHeader() || new HttpHeaders({
      "Content-Type": "application/json; charset=utf-8"
    });

    const businessName = this._globals.userInfo?.businessSetup?.[0]?.businessNameWithDomain;
    if (businessName) {
      headers = headers.set('BusinessName', businessName);
    }

    return this.http.post<any>(url, value, {
      headers,
      reportProgress: true,
      observe: 'events'
    }).pipe(map((event) => event));
  }

  // Backward compatibility wrapper
  uploadStoreContentSubmissionWithProgress(value: any): Observable<HttpEvent<any>> {
    return this.uploadStoreContentFileWithProgress(value);
  }

  // businessName param: sets the `BusinessName` header (mirrors uploadStoreContentFileWithProgress above).
  // value.BusinessNameWithDomain: same target business, sent in the body too since it's unconfirmed which
  // one `req.GetBusinessName()` actually reads — sending both is cheap insurance for admin callers acting
  // on a different business than their own. No record `Id` is needed — the backend updates the
  // StoreContent fields directly on that business's BusinessSetup record.
  updateStoreContentStatus(
    value: { Status: number; ShowNotificationBar: boolean; BusinessNameWithDomain?: string },
    businessName?: string
  ): Observable<any> {
    const url = environment.BusinessBaseURL + 'StoreContent/UpdateStatus';
    let headers = this._bs.GetAuthHeader() || new HttpHeaders({
      "Content-Type": "application/json; charset=utf-8"
    });

    if (businessName) {
      headers = headers.set('BusinessName', businessName);
    }

    return this.http.post<any>(url, value, { headers });
  }

  saveLoyaltyConfigSettings(value: LoyaltyConfig): Observable<any> {
    return this.businessHttpPost("SetBusinessLoyaltyConfiguration", value);
  }

  getLoyaltyConfigSettings(): Observable<any> {
    return this.businessHttpGet("GetBusinessLoyaltyConfiguration");
  }

  /** Selectable timezone options scoped to a business — GetTimeZoneOptions,
   *  GET account-svc business/timezoneoptions. Populates the timezone picker AND flags the
   *  business's currently active zone inline, so no separate business/timezone GET is needed.
   *  Requires business auth/header context like saveBusinessTimeZone below; pass businessName
   *  to target a business other than the logged-in user's own (e.g. an admin viewing a customer). */
  getBusinessTimeZoneOptions(businessName?: string): Observable<any> {
    const url = environment.BusinessBaseURL + 'business/timezoneoptions';
    return this._bs.httpGet(url, true, businessName || null);
  }

  /** Persists the picked timezone — writes into this business's BusinessSetup.TimeZones (Cosmos),
   *  flipping IsActive to just this entry. Payload uses the Windows time zone id (not IANA),
   *  matching CountryTimeZone.TimeZones' TZName/UTCOffset shape, e.g.
   *  { tzName: "Pakistan Standard Time", utcOffset: "+05:00", countryCode: "PK" }.
   *  businessName mirrors updateStoreContentStatus's BusinessName-header override for admin
   *  callers acting on a different business than their own. */
  saveBusinessTimeZone(
    value: { tzName: string; utcOffset: string; countryCode: string },
    businessName?: string
  ): Observable<any> {
    const url = environment.BusinessBaseURL + 'business/timezone';
    let headers = this._bs.GetAuthHeader() || new HttpHeaders({
      "Content-Type": "application/json; charset=utf-8"
    });

    if (businessName) {
      headers = headers.set('BusinessName', businessName);
    }

    return this.http.post<any>(url, value, { headers });
  }

  // showSpinner: false lets a caller that's already driving its own "please wait" state (e.g.
  // general.component.ts's saveFn(), which chains several requests together) suppress this call's
  // own auto-hide-on-completion — otherwise the spinner disappears the instant EditBusinessInfo
  // finishes, well before the rest of that chain (staff user creation, timezone save) even starts.
  saveBusinessInformation(value: any, showSpinner: boolean = true): Observable<any> {
    return this.businessHttpPost("EditBusinessInfo", value, showSpinner);
  }

  saveBusinessInformationV2(value: any): Observable<any> {
    return this.businessHttpPost("v2/EditBusinessInfoV2", value);
  }

  /** Same businessplan/edit endpoint the admin "Plan Configuration" tool uses
   *  (BizzCustomersService.editBusinessPlan) — confirmed via live network capture to be the real
   *  endpoint for updating StandardOnlineStore/POSLite/POSPro (and the other plan-entitlement
   *  fields it requires on every call) for an existing business. */
  editBusinessPlan(value: any): Observable<any> {
    return this.businessHttpPost("businessplan/edit", value);
  }

  notifyOnlineStoreInitiated(): void {
    if (this.isOnlineStoreInitiatedMarked() || this.setOnlineStoreInitiatedInFlight) {
      return;
    }

    this.setOnlineStoreInitiatedInFlight = true;
    const url = environment.AuthenticationURL + "businesssetup/online-store-initiated";
    const payload = {
      UserId: this.resolveAccountStatusUserId(),
      BusinessId: this.resolveAccountStatusBusinessId()
    };

    this._bs.httpPost(url, payload, false, false).subscribe({
      next: (res: any) => {
        if (res?.success) {
          this.markOnlineStoreInitiated();
        }
        this.setOnlineStoreInitiatedInFlight = false;
      },
      error: () => {
        this.setOnlineStoreInitiatedInFlight = false;
      }
    });
  }

  private getOnlineStoreInitiatedStorageKey(): string {
    const setup = this._globals.userInfo?.businessSetup?.[0] || {};
    const identity =
      setup?.businessId ||
      setup?.businessNameWithDomain ||
      setup?.businessName ||
      this._globals.userInfo?.userID ||
      this._globals.userInfo?.userId ||
      'default';
    return `online-store-initiated:${identity}`;
  }

  private isOnlineStoreInitiatedMarked(): boolean {
    try {
      return localStorage.getItem(this.getOnlineStoreInitiatedStorageKey()) === '1';
    } catch {
      return false;
    }
  }

  private markOnlineStoreInitiated(): void {
    try {
      localStorage.setItem(this.getOnlineStoreInitiatedStorageKey(), '1');
    } catch { }
  }

  // Theme Configuration
  getThemeConfigSettings(): Observable<any> {
    return this.businessHttpGet("webstore/configuration");
  }

  saveThemeInformation(value: any): Observable<any> {
    const payload = {
      ...(value || {}),
      env: value?.env ?? this.getEnvironmentLink()
    };

    return this.businessHttpPost("webstore/configuration", payload);
  }

  private getEnvironmentLink(): string {
    const endpointHint = String(
      environment.apiUrl || environment.AuthenticationURL || ''
    ).toLowerCase();

    if (
      endpointHint.includes('localhost')
      || endpointHint.includes('127.0.0.1')
      || endpointHint.includes('0.0.0.0')
    ) {
      return 'https://lsapimdev.azure-api.net';
    }

    if (endpointHint.includes('https://lsapim.azure-api.net')) {
      return 'https://lsapim.azure-api.net';
    }

    if (endpointHint.includes('https://lsapimstg.azure-api.net')) {
      return 'https://lsapimstg.azure-api.net';
    }

    if (endpointHint.includes('https://lsapimdev.azure-api.net')) {
      return 'https://lsapimdev.azure-api.net';
    }

    return String(environment.apiUrl || environment.AuthenticationURL || '')
      .replace(/\/?auth-svc\/api\/?$/i, '')
      .replace(/\/$/, '') || 'https://lsapimdev.azure-api.net';
  }

  saveAvailSaveOffer(
    value: { availSaveOffer: boolean },
    businessName?: string
  ): Observable<any> {
    const url = environment.BusinessBaseURL + "SaveAvailSaveOfferAsync";
    const resolvedBusinessName =
      businessName
      || this._globals.userInfo?.businessSetup?.[0]?.businessNameWithDomain
      || null;
    const normalizedValue = !!value?.availSaveOffer;
    const payload: any = {
      availSaveOffer: normalizedValue,
      AvailSaveOffer: normalizedValue
    };

    return this._bs.httpPost(url, payload, false, true, resolvedBusinessName).pipe(
      map((res) => res)
    );
  }

  requestCustomOnlineStore(value: {
    userId: string;
    businessId: number | string;
    userEmail: string;
    subdomain?: string;
    brandColors?: string[];
    onlineStoreTemplateId?: number;
    templateName?: string;
    templateImageUrl?: string;
    message?: string;
  }): Observable<any> {
    const url = environment.AuthenticationURL + "businesssetup/custom-online-store-request";
    return this._bs.httpPost(url, value, false, true);
  }

  saveSkipProductOnboarding(value: { skipProductOnboarding: boolean }): Observable<any> {
    const url = environment.BusinessBaseURL + "SaveSkipProductOnboardingAsync";
    const resolvedBusinessName =
      this._globals.userInfo?.businessSetup?.[0]?.businessNameWithDomain
      || null;
    const normalizedValue = !!value?.skipProductOnboarding;
    const payload = {
      skipProductOnboarding: normalizedValue,
      SkipProductOnboarding: normalizedValue
    };

    return this._bs.httpPost(url, payload, false, true, resolvedBusinessName).pipe(
      map((res) => res)
    );
  }

  /** GET ai-editor/active-editor — global "CurrentEditor" app-setting flag (WordPress/AI Editor),
   *  not per-business. No params: it's a single flag for the whole service, cached by the caller
   *  in GlobalService.activeEditor rather than re-fetched per business. */
  getActiveEditor(): Observable<any> {
    const url = environment.AuthenticationURL + 'ai-editor/active-editor';
    return this._bs.httpGet(url, false);
  }

  private activeEditor$: Observable<string> | null = null;

  /** Same flag as getActiveEditor(), but resolves synchronously-fast (of(cached)) once
   *  GlobalService.activeEditor is warm, and de-dupes concurrent in-flight fetches via
   *  shareReplay(1) — fixes a race where a component's own ngOnInit fetch hadn't resolved yet by
   *  the time a user action (e.g. typing a subdomain) needed to know which editor is active,
   *  silently falling back to the WordPress default for that one check. Callers that need to
   *  branch on the editor (not just display it) should go through this instead of reading
   *  GlobalService.activeEditor directly. */
  getActiveEditorCached(): Observable<string> {
    if (GlobalService.activeEditor) {
      return of(GlobalService.activeEditor);
    }

    if (!this.activeEditor$) {
      this.activeEditor$ = this.getActiveEditor().pipe(
        map((res: any) => String(res?.data?.editor ?? res?.editor ?? 'WordPress')),
        catchError(() => of('WordPress')),
        tap((editor) => {
          GlobalService.activeEditor = editor;
        }),
        shareReplay(1)
      );
    }

    return this.activeEditor$;
  }

  exchangeStoreEditorAuthCode(username: string, businessIdentifier: string): Observable<any> {
    const url = environment.AuthenticationURL + "ai-editor/b2c-exchange";
    return this._bs.httpPost(url, {}, false, true, null, false, {
      'x-username': username,
      'x-bussines-identifier': businessIdentifier,
      'x-bussiness-apikey': 'dummyapikey'
    });
  }

  /** A brand-new editor sign-in code, every time. The code is single-use: the editor tab exchanges it on arrival and
   *  it is spent. It used to be cached for a few minutes to save the (slow) exchange call, but that handed the second
   *  click a code that had already been used, so "Open my store" / "Edit your store" failed after the first time.
   *  Always ask for a new one. */
  getFreshStoreEditorAuthCode(username: string, businessIdentifier: string): Observable<any> {
    return this.exchangeStoreEditorAuthCode(username, businessIdentifier);
  }

  /** Creates the merchant's B2C storefront site — goes through the auth-svc gateway's
   *  online-store/create-site (not StoreEditorBaseURL directly), which forwards the
   *  x-username/x-bussiness-apikey headers as-is to editor.lincsell.com's b2c-create-site and, only
   *  on a genuine success (response.success and a url present), persists OnlineStoreCreated/
   *  OnlineStoreCreatedDate/OnlineStoreUrl on BusinessSetup and upserts WebStoreConfig itself —
   *  replaces the old two-call updatedomain()/saveThemeInformation() flow now that the "Pick a
   *  store template" picker is hidden and every merchant gets the same default template. Since the
   *  backend now sets OnlineStoreCreated directly, markDomainTemplateSaved() (called by the
   *  component on success) is just the same local fallback isDomainTemplateSaved already uses
   *  elsewhere, not the primary signal. */
  createB2CSite(
    userId: string,
    businessId: number | string,
    username: string,
    domain: string,
    templateId: string,
    brandColors?: string[],
    templateMeta?: CreateB2CSiteTemplateMeta
  ): Observable<any> {
    const url = environment.AuthenticationURL + "online-store/create-site";
    const body: any = { userId, businessId, domain, templateId };
    if (brandColors && brandColors.length > 0) {
      body.brandColors = brandColors;
    }
    if (templateMeta?.templateLabel) body.templateLabel = templateMeta.templateLabel;
    if (templateMeta?.templateThumbnailUrl) body.templateThumbnailUrl = templateMeta.templateThumbnailUrl;
    if (templateMeta?.templateCategory) body.templateCategory = templateMeta.templateCategory;
    if (templateMeta?.templateDemoUrl) body.templateDemoUrl = templateMeta.templateDemoUrl;
    if (templateMeta?.templatePrimaryColor) body.templatePrimaryColor = templateMeta.templatePrimaryColor;
    if (templateMeta?.templatePreviewImages && templateMeta.templatePreviewImages.length > 0) {
      body.templatePreviewImages = templateMeta.templatePreviewImages;
    }
    // Header name is genuinely 'x-bussiness-apikey' (typo, matching exchangeStoreEditorAuthCode()
    // above) — confirmed via a live 400 "x-bussiness-apikey header is required" when this was
    // sent as the correctly-spelled 'x-business-apikey'.
    return this._bs.httpPost(url, body, false, true, null, false, {
      'x-username': username,
      'x-bussiness-apikey': 'dummyapikey'
    });
  }

  markOnlineStoreCreated(payload: OnlineStoreCreatedPayload): Observable<any> {
    const url = environment.AuthenticationURL + 'businesssetup/online-store-created';
    return this._bs.httpPost(url, payload, false, true);
  }

  /** Admin action: updates the merchant's online-store template setup status (Customer Detail
   *  page). Same 'businesssetup/*' family as online-store-created/online-store-initiated/
   *  account-status-display above — sets OnlineStoreSetupStatus on BusinessSetup and, only for the
   *  'Completed' value, TemplateSetupCompleted (stamping TemplateSetupCompletedDate server-side).
   *  TemplateSetupCompleted is a one-way flag on the backend (never reset false once true), so it's
   *  only sent — as true — when status is 'Completed'; omitted otherwise per the API contract. */
  updateOnlineStoreSetupStatus(
    status: OnlineStoreSetupStatus,
    userId?: string | null,
    businessId?: number | string | null
  ): Observable<any> {
    const url = environment.AuthenticationURL + 'businesssetup/online-store-setup-status';
    const resolvedUserId = this.resolveAccountStatusUserId(userId);
    const resolvedBusinessId = this.resolveAccountStatusBusinessId(businessId);

    if (!resolvedUserId || resolvedBusinessId <= 0) {
      return of({
        Success: false,
        Message: 'UserId and BusinessId are required.'
      });
    }

    const payload: any = {
      UserId: resolvedUserId,
      BusinessId: resolvedBusinessId,
      OnlineStoreSetupStatus: status
    };
    if (status === 'Completed') {
      payload.TemplateSetupCompleted = true;
    }

    return this._bs.httpPost(url, payload, false, true).pipe(
      map((res) => res)
    );
  }

  /** Live subdomain-availability check, called as the merchant types (debounced) before they ever
   *  hit Save & next — same domain createB2CSite() creates the site on. Goes through the auth-svc
   *  gateway's online-store/check-domain — a thin pass-through to the editor service's own
   *  check-domain (unlike createB2CSite() above, which calls StoreEditorBaseURL directly) — so it
   *  only needs the normal bearer token the JwtInterceptor already attaches, not the
   *  x-username/x-business-apikey pair createB2CSite() needs. showSpinner is deliberately false; a
   *  full-page spinner on every keystroke check would be jarring, the caller shows its own small
   *  inline "Checking…" state instead. */
  checkDomainAvailability(domain: string): Observable<any> {
    const url = environment.AuthenticationURL + "online-store/check-domain";
    // skipInterceptor=true: a reserved/taken subdomain (e.g. SUBDOMAIN_RESERVED) is an expected
    // 400 here, not a real error — callers show it as a permanent inline hint under the subdomain
    // field instead of letting ErrorsInterceptor pop its transient toast for it.
    return this._bs.httpGetParams(url, { domain }, false, false, true);
  }

  /** AI-editor's template catalog — GET auth-svc online-store/templates, a thin pass-through to
   *  editor.lincsell.com's own template list (same x-username/x-bussiness-apikey header pattern as
   *  createB2CSite()/exchangeStoreEditorAuthCode() above). auth=true so the normal bearer token is
   *  attached alongside those headers, matching the endpoint's documented requirements. Populates
   *  the "Choose your store template" picker for AI-editor accounts, which previously only ever
   *  showed a single hardcoded 'classic' placeholder since no catalog existed yet. */
  getOnlineStoreTemplates(username: string, businessIdentifier: string, status: string = 'published'): Observable<any> {
    const url = environment.AuthenticationURL + 'online-store/templates';
    return this._bs.httpGetParams(url, { status }, true, false, false, {
      'x-username': username,
      'x-business-identifier': businessIdentifier,
      'x-bussiness-apikey': 'dummyapikey'
    });
  }

  saveSkipedProductOnboardingOfferDisplayedOnOnlineStorePage(
    value: {
      SkipedProductOnboardingOfferDisplayedOnOnlineStorePage: boolean;
      UserId?: string | null;
      BusinessId?: number | string | null;
    }
  ): Observable<any> {
    const url = environment.BusinessBaseURL + "SaveSkipedProductOnboardingOfferDisplayedAsync";
    const resolvedBusinessName =
      this._globals.userInfo?.businessSetup?.[0]?.businessNameWithDomain
      || null;
    const normalizedValue = !!value?.SkipedProductOnboardingOfferDisplayedOnOnlineStorePage;
    const payload = {
      SkipedProductOnboardingOfferDisplayedOnOnlineStorePage: normalizedValue,
      UserId: this.resolveAccountStatusUserId(value?.UserId),
      BusinessId: this.resolveAccountStatusBusinessId(value?.BusinessId)
    };

    return this._bs.httpPost(url, payload, false, true, resolvedBusinessName).pipe(
      map((res) => res)
    );
  }

  saveAccountStatusDisplay(
    status: 'Onboarding' | 'Active' | 'Inactive' | string,
    userId?: string | null,
    businessId?: number | string | null
  ): Observable<any> {
    const url = environment.AuthenticationURL + "businesssetup/account-status-display";
    const resolvedStatus = this.normalizeAccountStatusDisplay(status);
    const resolvedUserId = this.resolveAccountStatusUserId(userId);
    const resolvedBusinessId = this.resolveAccountStatusBusinessId(businessId);
    const payload = {
      UserId: resolvedUserId,
      BusinessId: resolvedBusinessId,
      AccountStatusDisplay: resolvedStatus
    };

    return this._bs.httpPost(url, payload, false, false, null, true).pipe(
      map((res) => res)
    );
  }

  private transactionConfigInFlight = false;

  saveTransactionConfig(
    planCosmosId: string,
    userId?: string | null,
    businessId?: number | string | null,
    sellingChannels?: { hasPOS: boolean; hasPOSLite: boolean; standardOnlineStore: boolean }
  ): Observable<any> {
    const url = environment.AuthenticationURL + "businesssetup/transaction-config";
    const resolvedUserId = this.resolveAccountStatusUserId(userId);
    const resolvedBusinessId = this.resolveAccountStatusBusinessId(businessId);
    const resolvedPlanCosmosId = String(planCosmosId ?? '').trim();

    // Never send an incomplete payload. On a re-login the caller can fire before the logged-in
    // user is hydrated (empty userId / businessId 0) — skip WITHOUT setting the dedupe flag so a
    // later, fully-populated call still goes through.
    if (!resolvedUserId || resolvedBusinessId <= 0 || !resolvedPlanCosmosId) {
      return of(null);
    }

    // Send at most once per business. This call fires from several points (the payment-success
    // "Go to Dashboard" button, the store-setup-offer page load, and its Submit/Skip buttons) so
    // whichever the merchant reaches first sends it; later calls short-circuit. Only a successful
    // send sets the flag, so a network failure will retry on the next trigger.
    const sentKey = `transaction-config-sent-${resolvedUserId}-${resolvedBusinessId}`;
    if (localStorage.getItem(sentKey) === '1') {
      return of(null);
    }

    // Synchronous in-flight guard: the localStorage flag is only set on the response, so two
    // near-simultaneous triggers (e.g. the "Go to Dashboard" button firing it, then the
    // store-setup-offer page it navigates to firing it again) would both pass the flag check
    // before either completes. This blocks the concurrent duplicate.
    if (this.transactionConfigInFlight) {
      return of(null);
    }
    this.transactionConfigInFlight = true;

    const payload: any = {
      userId: resolvedUserId,
      businessId: resolvedBusinessId,
      planCosmosId: resolvedPlanCosmosId
    };
    if (sellingChannels) {
      payload.hasPOS = sellingChannels.hasPOS;
      payload.hasPOSLite = sellingChannels.hasPOSLite;
      payload.standardOnlineStore = sellingChannels.standardOnlineStore;
    }

    return this._bs.httpPost(url, payload, false, false, null, true).pipe(
      tap((res: any) => {
        // Treat any non-error response as success (backend returns success flags inconsistently).
        if (res === null || res === undefined || res?.success !== false) {
          try { localStorage.setItem(sentKey, '1'); } catch { /* ignore storage errors */ }
        }
      }),
      finalize(() => { this.transactionConfigInFlight = false; }),
      map((res) => res)
    );
  }

  private sellingChannelsInFlight = false;

  /** Persists the merchant's own sell-channel choice (signup-v6's sales-channel step) via its
   *  dedicated endpoint. transaction-config's hasPOS/hasPOSLite/standardOnlineStore fields reflect
   *  whatever was true when that call happened to fire — this call is the source of truth for the
   *  merchant's actual selection and should run right after transaction-config, once per business. */
  saveSellingChannels(
    userId?: string | null,
    businessId?: number | string | null,
    sellingChannels?: { hasPOS: boolean; hasPOSLite: boolean; standardOnlineStore: boolean }
  ): Observable<any> {
    const url = environment.AuthenticationURL + "businesssetup/selling-channels";
    const resolvedUserId = this.resolveAccountStatusUserId(userId);
    const resolvedBusinessId = this.resolveAccountStatusBusinessId(businessId);

    if (!resolvedUserId || resolvedBusinessId <= 0 || !sellingChannels) {
      return of(null);
    }

    const sentKey = `selling-channels-sent-${resolvedUserId}-${resolvedBusinessId}`;
    if (localStorage.getItem(sentKey) === '1') {
      return of(null);
    }

    if (this.sellingChannelsInFlight) {
      return of(null);
    }
    this.sellingChannelsInFlight = true;

    const payload = {
      userId: resolvedUserId,
      businessId: resolvedBusinessId,
      hasPOS: sellingChannels.hasPOS,
      hasPOSLite: sellingChannels.hasPOSLite,
      standardOnlineStore: sellingChannels.standardOnlineStore
    };

    return this._bs.httpPost(url, payload, false, false, null, true).pipe(
      tap((res: any) => {
        if (res === null || res === undefined || res?.success !== false) {
          try { localStorage.setItem(sentKey, '1'); } catch { /* ignore storage errors */ }
        }
      }),
      finalize(() => { this.sellingChannelsInFlight = false; }),
      map((res) => res)
    );
  }

  private normalizeAccountStatusDisplay(status: string): 'Onboarding' | 'Active' | 'Inactive' {
    const raw = String(status || '').trim().toLowerCase();
    if (raw === 'active' || raw === 'otpverified' || raw === 'otp_verified') {
      return 'Active';
    }
    if (raw === 'inactive') {
      return 'Inactive';
    }
    return 'Onboarding';
  }

  private resolveAccountStatusUserId(userId?: string | null): string {
    const fromArg = String(userId ?? '').trim();
    if (fromArg) {
      return fromArg;
    }

    const fromGlobal = String(
      this._globals.userInfo?.userID
      ?? this._globals.userInfo?.userId
      ?? ''
    ).trim();
    if (fromGlobal) {
      return fromGlobal;
    }

    const fromPersonal = this.safeParseSession<any>('personal-Info');
    const personalUserId = String(fromPersonal?.userID ?? fromPersonal?.userId ?? '').trim();
    if (personalUserId) {
      return personalUserId;
    }

    const fromRequired = this.safeParseSession<any>('requiredInfo');
    return String(fromRequired?.UserId ?? fromRequired?.userId ?? '').trim();
  }

  private resolveAccountStatusBusinessId(businessId?: number | string | null): number {
    const parseId = (value: any): number => {
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    };

    const argParsed = parseId(businessId);
    if (argParsed > 0) {
      return argParsed;
    }

    const globalParsed = parseId(
      this._globals.userInfo?.businessSetup?.[0]?.businessId
      ?? this._globals.userInfo?.businessSetup?.[0]?.id
    );
    if (globalParsed > 0) {
      return globalParsed;
    }

    const fromBusinessSession = this.safeParseSession<any>('business-Info');
    const sessionParsed = parseId(fromBusinessSession?.businessId ?? fromBusinessSession?.id);
    if (sessionParsed > 0) {
      return sessionParsed;
    }

    const fromRequired = this.safeParseSession<any>('requiredInfo');
    return parseId(fromRequired?.BusinessId ?? fromRequired?.businessId);
  }

  private safeParseSession<T = any>(key: string): T | null {
    try {
      const value = sessionStorage.getItem(key);
      if (!value) {
        return null;
      }
      return JSON.parse(value) as T;
    } catch {
      return null;
    }
  }

  saveEditStorePopupPreference(
    value: { dontShowAgain: boolean },
    businessName?: string
  ): Observable<any> {
    const endpoint = environment.SaveEditStorePopupPreference || "SaveEditStorePopupPreferenceAsync";
    const url = environment.BusinessBaseURL + endpoint;
    const resolvedBusinessName =
      businessName
      || this._globals.userInfo?.businessSetup?.[0]?.businessNameWithDomain
      || null;
    const normalizedValue = !!value?.dontShowAgain;
    const payload: any = {
      dontShowAgain: normalizedValue,
      DontShowAgain: normalizedValue
    };

    return this._bs.httpPost(url, payload, false, true, resolvedBusinessName).pipe(
      map((res) => res)
    );
  }

  // Widget configuration save methods
  saveTaxSetting(value: TaxConfig): Observable<any> {
    return this.businessHttpPost("SetBTaxConfig", value);
  }

  /** Default tax rates per US state/region, used to suggest a rate when a state is added in the tax setup UI. */
  getTaxRateDefault(): Observable<any> {
    return this.businessHttpGet("GetTaxRateDefault");
  }

  /** First-time save of the TaxSettings/TaxRate schema for a store (tax-setting-v2's shipping/nexus mode + per-state rate table). */
  createTaxSettings(value: any): Observable<any> {
    return this.businessHttpPost("CreateTaxSettings", value);
  }

  /** Updates an existing store's TaxSettings row — used once GetTaxSettings has returned a saved record. */
  updateTaxSettings(value: any): Observable<any> {
    return this.businessHttpPut("UpdateTaxSettings", value);
  }

  /** Loads the saved per-store tax settings (collectTax, taxBasis, disclaimer, state rates) for tax-setting-v2. */
  getTaxSettings(): Observable<any> {
    return this.businessHttpGet("GetTaxSettings");
  }

  /** Per-state/location tax rate rows (tax-setting-v2's "States you collect tax in" / "Your business locations" table). */
  getTaxRate(): Observable<any> {
    return this.businessHttpGet("GetTaxRate");
  }

  createTaxRate(value: any): Observable<any> {
    return this.businessHttpPost("CreateTaxRate", value);
  }

  updateTaxRate(value: any): Observable<any> {
    return this.businessHttpPut("UpdateTaxRate", value);
  }

  deleteTaxRate(id: number): Observable<any> {
    // id is a route/path parameter on this endpoint (DeleteTaxRate/{id}), not a query string.
    return this.businessHttpDelete(`DeleteTaxRate/${id}`);
  }

  saveInvoiceConfiguration(value: CashHandlingConfig): Observable<any> {
    return this.businessHttpPost("SetBCashHandlingConfig", value);
  }

  savePrintingConfiguration(value: PrintingConfig): Observable<any> {
    return this.businessHttpPost("SetBPrintingConfig", value);
  }

  saveSecurityConfiguration(value: SecurityConfig): Observable<any> {
    return this.businessHttpPost("SetBSecurityConfig", value);
  }

  saveCashdropConfiguration(value: CashDropConfig): Observable<any> {
    return this.businessHttpPost("SetBCashDropConfig", value);
  }

  saveCashdrawerConfiguration(value: CashDrawerConfig): Observable<any> {
    return this.businessHttpPost("SetBCashDrawerConfig", value);
  }

  saveAdminTenderConfiguration(queryString: any, value: TenderScreenConfig): Observable<any> {
    return this.businessHttpPost(`PTenderConfig?${queryString}`, value);
  }

  saveTenderConfiguration(queryString: any, value: TenderScreenConfig): Observable<any> {
    return this.businessHttpPost(`PTenderConfig?${queryString}`, value);
  }

  saveTimeTrackingConfiguration(value: TimeTrackingConfig): Observable<any> {
    return this.businessHttpPost("SetTimeTracking", value);
  }

  // Lifecycle methods
  ngOnInit(): void {
    this.initActiveItem();
  }

    SetupConnectAccount(value: any): Observable<any> {
    const url = environment.BusinessBaseURL + "createstripeconnectonboarding";
    return this._bs.httpPost(url,value).pipe(
        map((res) => res)
        // You can add error handling here if needed
    );
  }

  /** Shared orchestration for kicking off Stripe Connect onboarding — same accountConfig shape
   *  and popup behavior the business-setup checklist's "Connect Stripe Account" button uses, so
   *  callers that aren't the checklist itself (e.g. the trial-lockout overlay) trigger the exact
   *  same flow instead of only deep-linking to the checklist and requiring a second click there. */
  startStripeOnboarding(): Observable<{ onboardingUrl?: string; success: boolean; message?: string }> {
    let websiteDomain = window.location.origin + '/';
    const n = websiteDomain.lastIndexOf('/');
    if (websiteDomain.substring(n + 1) === '') {
      websiteDomain = websiteDomain.substring(0, websiteDomain.length - 1);
    }
    const accountConfig = {
      Email: GlobalService.userInfo.username,
      ReturnUrl: websiteDomain + '/success',
      RefreshUrl: websiteDomain + '/reauth',
      UserId: GlobalService.userInfo.businessSetup[0].userID
    };
    return this.SetupConnectAccount(accountConfig).pipe(
      map((response: any) => {
        if (response?.data?.onboardingUrl) {
          return { onboardingUrl: response.data.onboardingUrl, success: true };
        }
        return { success: false, message: response?.message || 'Failed to initiate Stripe account connection.' };
      })
    );
  }

    CheckConnectAccount(): Observable<any> {
    const url = environment.BusinessBaseURL + "GetConnectStatus";
      return this._bs.httpGet(url, true, null, true).pipe(
          map((res: any) => {
            if (res?.success) {
              return res;
            }

            return {
              success: false,
              data: {
                chargesEnabled: false,
                status: 'unavailable'
              },
              message: res?.message || 'Unable to get connect account status.'
            };
          })
      );
  }

  updateWordPressDomain(email: string, businessName: string, businessId: string | number, brandColors?: string[]): Observable<any> {
    const body: any = {
      email,
      business_name: businessName,
      business_id: businessId
    };
    if (brandColors && brandColors.length > 0) {
      body.brand_colors = brandColors;
    }
    const url = `https://lsapimdev.azure-api.net/auth-svc/api/product-url`;
    return this.http.get<any>(url, {
      params: {
        url: 'https://lincsell.com/wp-json/custom-api/v1/updatedomain',
        method: 'POST',
        body: JSON.stringify(body)
      }
    });
  }


}
